import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { verificationApi } from '../api';
import { dateOnly } from '../utils';

function VerifyRecord() {
  const { type, id } = useParams();
  const [record, setRecord] = useState(null);
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    const load = async () => {
      setStatus('checking');
      try {
        const data = await verificationApi.get(type, id);
        setRecord(data);
        setStatus(data?.verified ? 'valid' : 'invalid');
      } catch (error) {
        setStatus('invalid');
      }
    };
    load();
  }, [type, id]);

  const isValid = status === 'valid';

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-8 text-slate-950 dark:bg-slate-950 dark:text-slate-50 sm:px-6">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-5xl items-center">
        <section className="grid w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl animate-float-in dark:border-slate-800 dark:bg-slate-900 lg:grid-cols-[0.95fr_1.05fr]">
          <div className="bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-500 p-6 text-white sm:p-8">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-white/15 text-sm font-black">CR</div>
            <h1 className="mt-10 text-3xl font-black sm:text-4xl">CloudRecords Verification</h1>
            <p className="mt-4 max-w-sm text-sm leading-6 text-white/80">
              Public record verification for secure student, employee and institutional document identities.
            </p>
          </div>

          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-4">
              <div className={`grid h-16 w-16 place-items-center rounded-full text-xl font-black text-white transition-all duration-300 ${isValid ? 'bg-emerald-600 shadow-lg shadow-emerald-500/25' : status === 'invalid' ? 'bg-rose-600' : 'bg-slate-400 animate-pulse-soft'}`}>
                {isValid ? '✓' : status === 'invalid' ? '!' : '...'}
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-[0.35em] text-slate-400">Verification Status</p>
                <h2 className="mt-1 text-2xl font-black">{isValid ? 'Verified Record' : status === 'invalid' ? 'Record Not Found' : 'Checking Record'}</h2>
              </div>
            </div>

            {status === 'checking' && (
              <div className="mt-8 space-y-3">
                <div className="skeleton h-4 w-3/4 rounded" />
                <div className="skeleton h-4 w-1/2 rounded" />
                <div className="skeleton h-24 w-full rounded-lg" />
              </div>
            )}

            {status === 'invalid' && (
              <div className="mt-8 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm font-bold text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200">
                No matching {type} record was found for {id}. Please check the QR code or contact the issuing institution.
              </div>
            )}

            {isValid && (
              <div className="mt-8 space-y-4">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-black text-emerald-700 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200">
                  This is an active verified {record.type || type} record issued through CloudRecords.
                </div>
                {Object.entries({
                  ID: record.id,
                  Name: record.name,
                  Department: record.department,
                  Role: record.role,
                  Created: dateOnly(record.createdAt)
                }).map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-4 border-b border-slate-100 py-3 text-sm dark:border-slate-800">
                    <span className="font-bold text-slate-400">{label}</span>
                    <span className="text-right font-black text-slate-800 dark:text-slate-100">{value || '-'}</span>
                  </div>
                ))}
              </div>
            )}

            <Link to="/" className="mt-8 inline-flex min-h-11 items-center rounded-lg bg-slate-950 px-5 py-2 text-sm font-black text-white transition-all duration-150 hover:bg-slate-800 active:scale-[0.97] dark:bg-white dark:text-slate-950">
              Back to CloudRecords
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}

export default VerifyRecord;
